/**
 * Demo data inject/cleanup for the Machines > Performance segment of the Welcome Tour.
 *
 * Injects:
 * - Demo machine into machines list + selectedItem
 * - Machine statistics into Redux (for sidebar + chart header)
 * - NC file statistics into Redux (for NC file breakdown)
 * - Machine stats hour into registry (intercepted by getMachineStatsPerPeriod guard)
 */

import { AppThunk } from '@store/types';
import {
  setSelectedMachine,
  injectDemoMachine,
  removeDemoMachine,
  setMachineStatistics,
  setNCFileStatistics,
} from '../../../../../../reducers/machines';
import {
  registerDemoMachineStatsHour,
  registerDemoMachineStatsHourPrev,
  clearDemoRegistry,
  setTourActive,
} from '../../../../helpers/demoDataRegistry';
import { DEMO_WELCOME_MACHINE_ID } from '@components/tours/config/global/welcome/constants';
import {
  createDemoMachine,
  createDemoMachineStatistics,
  createDemoNcFileStatistics,
  createDemoMachineStatsHour,
  createDemoPreviousMachineStatsHour,
  NC_FILE_SPLIT,
  NC_FILE_SPLIT_PREV,
} from './fixtures';

/**
 * Inject demo data for the Machines > Performance segment.
 */
export const injectPerformanceWelcomeDemo = (): AppThunk => (dispatch) => {
  // Block real API calls during tour
  setTourActive(true);

  // Create demo data
  const demoMachine = createDemoMachine();
  const stats = createDemoMachineStatistics();
  const ncFileStats = createDemoNcFileStatistics();
  const chartData = createDemoMachineStatsHour();
  const prevChartData = createDemoPreviousMachineStatsHour();

  // Compute sidebar values from chart data so they match the chart header exactly
  const sumField = (data: any[], field: string) => data.reduce((sum: number, e: any) => sum + (e[field] || 0), 0);

  const totalActiveSecs = sumField(chartData, 'totalActiveSecs');
  const totalSetupSecs = sumField(chartData, 'totalSetupSecs');
  const totalChangeoverSecs = sumField(chartData, 'totalChangeSecs');
  const totalProductiveSecs = totalActiveSecs + totalSetupSecs + totalChangeoverSecs;

  const totalActiveSecsPrevious = sumField(prevChartData, 'totalActiveSecs');
  const totalSetupSecsPrevious = sumField(prevChartData, 'totalSetupSecs');
  const totalChangeoverSecsPrevious = sumField(prevChartData, 'totalChangeSecs');
  const totalProductiveSecsPrevious = totalActiveSecsPrevious + totalSetupSecsPrevious + totalChangeoverSecsPrevious;

  // Register chart data in registry (getMachineStatsPerPeriod guard will return it)
  registerDemoMachineStatsHour(DEMO_WELCOME_MACHINE_ID, chartData);
  registerDemoMachineStatsHourPrev(DEMO_WELCOME_MACHINE_ID, prevChartData);

  // Inject demo machine into machines list (for left sidebar)
  dispatch(injectDemoMachine(demoMachine));
  dispatch(setSelectedMachine(demoMachine));

  // Dispatch machine statistics with computed values so sidebar matches chart
  const maxValueActive = Math.max(totalActiveSecs, totalActiveSecsPrevious);
  const maxValueProductive = Math.max(totalProductiveSecs, totalProductiveSecsPrevious);

  dispatch(
    setMachineStatistics({
      data: [{
        ...demoMachine,
        machineStatistics: {
          ...stats[0],
          totalActiveSecs,
          totalProductiveSecs,
          totalChangeoverSecs,
          totalSetupSecs,
          totalActiveSecsPrevious,
          totalProductiveSecsPrevious,
          totalLoaded: true,
        },
      }],
      maxValueActive,
      maxValueProductive,
      loaded: true,
    }),
  );

  // Compute NC file statistics proportionally from chart totals so they sum correctly
  const splits = [NC_FILE_SPLIT.first, NC_FILE_SPLIT.second];
  const splitsPrev = [NC_FILE_SPLIT_PREV.first, NC_FILE_SPLIT_PREV.second];

  const computedNcFileStats = ncFileStats.map((nf, i) => ({
    ...nf,
    totalActiveSecs: Math.round(totalActiveSecs * splits[i]),
    totalSetupSecs: Math.round(totalSetupSecs * splits[i]),
    totalChangeoverSecs: Math.round(totalChangeoverSecs * splits[i]),
    totalProductiveSecs: Math.round(totalProductiveSecs * splits[i]),
    totalActiveSecsPrevious: Math.round(totalActiveSecsPrevious * splitsPrev[i]),
    totalProductiveSecsPrevious: Math.round(totalProductiveSecsPrevious * splitsPrev[i]),
  }));

  const ncMaxActive = Math.max(...computedNcFileStats.map((f) => Math.max(f.totalActiveSecs, f.totalActiveSecsPrevious)));
  const ncMaxProductive = Math.max(...computedNcFileStats.map((f) => Math.max(f.totalProductiveSecs, f.totalProductiveSecsPrevious)));

  // Dispatch NC file statistics
  dispatch(
    setNCFileStatistics({
      data: computedNcFileStats,
      page: 1,
      isMore: false,
      maxValueActive: ncMaxActive,
      maxValueProductive: ncMaxProductive,
    }),
  );
};

/**
 * Cleanup demo data after Performance segment ends.
 */
export const cleanupPerformanceWelcomeDemo = (): AppThunk => (dispatch) => {
  // Clear registry + tour active flag
  clearDemoRegistry();
  setTourActive(false);

  // Remove demo machine
  dispatch(removeDemoMachine(DEMO_WELCOME_MACHINE_ID));

  // Reset statistics state
  dispatch(
    setMachineStatistics({
      data: [],
      maxValueActive: 0,
      maxValueProductive: 0,
      loaded: false,
    }),
  );

  dispatch(
    setNCFileStatistics({
      data: [],
      page: 1,
      isMore: false,
      maxValueActive: 0,
      maxValueProductive: 0,
    }),
  );

  dispatch(setSelectedMachine({}));
};
